import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Users, Trophy, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
const benefits = [{
  icon: Calendar,
  title: 'Event-Ready',
  description: 'Meet tight deadlines for races, tournaments, and events'
}, {
  icon: Users,
  title: 'Group Coordination',
  description: 'Easy sizing and ordering for teams and organizations'
}, {
  icon: Trophy,
  title: 'Spirit & Pride',
  description: 'Apparel that supporters are proud to wear'
}];
const faqs = [{
  question: 'Can you meet tight event deadlines?',
  answer: "Yes. We understand event timelines and offer rush production for time-sensitive projects. Contact us as soon as possible to discuss your deadline and we'll work to accommodate your schedule."
}, {
  question: 'How do group orders and sizing work?',
  answer: "We can set up online stores for easy group ordering with individual sizing, or you can collect sizes and place a single bulk order. We'll help you choose the best approach for your team or organization."
}, {
  question: 'Do you support fundraising programs?',
  answer: 'Yes. We work with schools, teams, and organizations on fundraising apparel. We can help structure orders to maximize fundraising potential while keeping costs manageable.'
}, {
  question: 'Can we reorder the same design later?',
  answer: 'Absolutely. We keep your artwork and specifications on file, making reorders fast and consistent. This is especially helpful for recurring events, new team members, or additional fundraising runs.'
}, {
  question: 'What types of teams and events do you work with?',
  answer: 'We work with racing teams, school sports, clubs, reunions, weddings, tournaments, fundraisers, and community events across Michigan. Our process is flexible enough to support any group or occasion.'
}, {
  question: 'Do you have minimum order requirements?',
  answer: 'No minimums. Whether you need a few shirts for a small event or a full team run, we support orders of any size.'
}];
export function TeamApparel() {
  return <div className="min-h-screen bg-atomic-black" data-id="element-1244">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden bg-gradient-to-br from-atomic-charcoal via-atomic-black to-atomic-black" data-id="element-1245">
        <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'linear-gradient(rgba(163, 230, 53, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(163, 230, 53, 0.1) 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }} data-id="element-1246" />
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1247">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-3xl mx-auto text-center" data-id="element-1248">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-1249">
              Team & Event Apparel for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-atomic-green to-lime-400" data-id="element-1250">
                Schools, Sports & Fundraisers
              </span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8" data-id="element-1251">
              Custom apparel for teams, schools, events, and fundraisers across
              Michigan. Fast turnaround and easy coordination for groups of any
              size.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1252">
              <a href="/contact" data-id="element-1253">
                <Button size="lg" data-id="element-1254">
                  Request Team Quote
                  <ArrowRight className="ml-2 w-4 h-4" data-id="element-1255" />
                </Button>
              </a>
              <a href="/catalog" data-id="element-1256">
                <Button variant="outline" size="lg" data-id="element-1257">
                  View Options
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-atomic-black border-t border-white/5" data-id="element-1258">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1259">
          <div className="max-w-5xl mx-auto" data-id="element-1260">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-1261">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1262">
                Why Teams Choose Us
              </h2>
              <p className="text-lg text-gray-300" data-id="element-1263">
                Fast turnaround, easy coordination, and quality results
              </p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-8" data-id="element-1264">
              {benefits.map((benefit, index) => <motion.div key={benefit.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-atomic-charcoal rounded-xl p-6 border border-white/10" data-id="element-1265">
                  <div className="w-12 h-12 rounded-lg bg-atomic-green/10 flex items-center justify-center text-atomic-green mb-4 border border-atomic-green/20" data-id="element-1266">
                    <benefit.icon size={24} data-id="element-1267" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-1268">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1269">
                    {benefit.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-atomic-charcoal border-t border-white/5" data-id="element-1270">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1271">
          <div className="max-w-3xl mx-auto" data-id="element-1272">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-1273">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1274">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-300" data-id="element-1275">
                Common questions about team and event apparel
              </p>
            </motion.div>
            <div className="space-y-6" data-id="element-1276">
              {faqs.map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="bg-atomic-black rounded-xl p-6 border border-white/10" data-id="element-1277">
                  <h3 className="text-lg font-bold text-white mb-3" data-id="element-1278">
                    {faq.question}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1279">{faq.answer}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}